import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { sendOTP } from "../utils/otpService";
import { apiClient, encryptData } from "../utils/const";
import { usePermissions } from "./PermissionContext";

function OTPVerification() {
  const navigate = useNavigate();
  const location = useLocation();
  const { updatePermissions } = usePermissions();
  
  // Get data from location state
  const { phone, generatedOtp } = location.state || {};
  
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [otpTimer, setOtpTimer] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  // Prevent body scrolling
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  // OTP Timer effect
  useEffect(() => {
    let interval = null;
    if (otpTimer > 0) {
      interval = setInterval(() => {
        setOtpTimer(timer => timer - 1);
      }, 1000);
    } else if (otpTimer === 0) {
      setCanResend(true);
    }
    return () => clearInterval(interval);
  }, [otpTimer]);

  // Redirect if no phone number
  useEffect(() => {
    if (!phone) {
      navigate("/login");
    }
  }, [phone, navigate]);

  const validateOTP = (value) => {
    if (!value.trim()) {
      return "OTP is required";
    } else if (!/^\d{6}$/.test(value.trim())) {
      return "Please enter a valid 6-digit OTP";
    }
    return "";
  };

  const handleChange = (e) => {
    const value = e.target.value;
    setOtp(value);
    
    // Real-time validation
    const error = validateOTP(value);
    setValidationErrors({ otp: error });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Validate OTP
    const otpError = validateOTP(otp);
    if (otpError) {
      setValidationErrors({ otp: otpError });
      toast.error("Please enter a valid 6-digit OTP", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      setLoading(false);
      return;
    }

    // Verify OTP
    if (otp === generatedOtp) {
      try {


        console.log("phone", phone);
        console.log("otp", otp);

        // Call login API with mobile and OTP
        const loginResponse = await apiClient.post('/login.php', {
          mobile: phone,
          otp: otp
        });

        console.log("loginResponse", loginResponse.data);
        // console.log("loginResponse", loginResponse.data);
        if (loginResponse.data.success) {
          // Store user data, permissions, and token in localStorage
          const { user, token, permissions } = loginResponse.data.data;
          
          // Encrypt permissions before storing
          const encryptedPermissions = encryptData(permissions);
          
          localStorage.setItem("user", JSON.stringify(user));
          localStorage.setItem("token", token);
          localStorage.setItem("permissions", encryptedPermissions);
          localStorage.setItem("schemeType", "mandal");
          
          // Update permissions context with decrypted permissions
          updatePermissions(permissions);
          
          toast.success("OTP verified successfully! Redirecting to dashboard...", {
            position: "top-right",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          });
          
          setTimeout(() => {
            navigate("/dashboard");
          }, 1000);
        } else {
          throw new Error(loginResponse.data.message || "Login failed");
        }
      } catch (error) {
        console.error("Login error:", error);
        toast.error(error.response?.data?.message || "Login failed. Please try again.", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
        setLoading(false);
      }
    } else {
      toast.error("Invalid OTP. Please try again.", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      setLoading(false);
    }
  };

  // Resend OTP function
  const handleResendOTP = async () => {
    if (!canResend) return;
    
    setLoading(true);
    const otpResult = await sendOTP(phone);
    
    if (otpResult.success) {
      setOtpTimer(60);
      setCanResend(false);
      toast.success("OTP resent successfully", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } else {
      toast.error(otpResult.message || "Failed to resend OTP", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }
    setLoading(false);
  };

  return (
    <>
      <div className="container-xxl h-100" style={{overflow: 'hidden'}}>
        <div className="row h-100 d-flex justify-content-center align-items-center">
          <div className="col-12">
            <div className="card-body p-0">
              <div className="row justify-content-center">
                <div className="col-lg-4 mb-5">
                  <div className="card">
                    <div className="card-body p-0 auth-header-box rounded-top" style={{backgroundColor: '#4B2C3B', marginTop: '1.5rem', marginBottom: '-1.5rem'}}>
                      <div className="text-center p-3">
                        <a href="index.html" className="logo logo-admin">
                          <img
                            src="assets/images/or_logo.png"
                            height={100}
                            alt="logo"
                            className="auth-logo"
                          />
                        </a>
                        <h4 className="mt-2 mb-1 fw-semibold fs-18" style={{color: '#D3AF37'}}>
                          Verify Your Mobile Number
                        </h4>
                      </div>
                    </div>
                    <div className="card-body pt-3 px-4">
                      <form className="my-4" onSubmit={handleSubmit}>
                        <div className="text-center mb-3">
                          <h5 className="text-primary">Enter Verification Code</h5>
                          <p className="text-muted mb-0" style={{fontSize: '0.9rem'}}>
                            We've sent a 6-digit OTP to your mobile number ending with {phone?.slice(-4)}
                          </p>
                        </div>
                        
                        <div className="form-group mb-2">
                          <label className="form-label" htmlFor="otp">
                            Enter OTP
                          </label>
                          <input
                            type="text"
                            className={`form-control text-center ${
                              validationErrors.otp ? "is-invalid" : ""
                            }`}
                            id="otp"
                            name="otp"
                            placeholder="Enter 6-digit OTP"
                            value={otp}
                            onChange={handleChange}
                            maxLength="6"
                            style={{fontSize: '1.2rem', letterSpacing: '0.2em'}}
                          />
                          {validationErrors.otp && (
                            <div className="invalid-feedback">
                              {validationErrors.otp}
                            </div>
                          )}
                        </div>
                        
                        <div className="text-center mb-2">
                          {otpTimer > 0 ? (
                            <p className="text-muted mb-1" style={{fontSize: '0.9rem'}}>
                              Resend OTP in {otpTimer} seconds
                            </p>
                          ) : (
                            <button
                              type="button"
                              className="btn btn-link text-primary p-0"
                              onClick={handleResendOTP}
                              disabled={!canResend || loading}
                              style={{fontSize: '0.9rem'}}
                            >
                              Resend OTP
                            </button>
                          )}
                        </div>

                        <div className="form-group mb-0 row">
                          <div className="col-12">
                            <div className="d-grid mt-2">
                              <button
                                className="btn mb-2"
                                type="submit"
                                disabled={loading}
                                style={{backgroundColor: '#4B2C3B', color: '#D3AF37'}}
                              >
                                {loading ? "Verifying..." : "Verify OTP"}
                                <i className="fas fa-check ms-1" />
                              </button>
                              <button
                                type="button"
                                className="btn btn-outline-secondary"
                                onClick={() => navigate("/login")}
                              >
                                <i className="fas fa-arrow-left me-1" /> Back to Login
                              </button>
                            </div>
                          </div>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default OTPVerification;
